import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import {
  SubProject,
  SUB_PROJECT_TYPES,
} from 'src/app/model/project/sub-project';
import { Tour } from 'src/app/model/tour/tour';
import { TourItem } from 'src/app/model/tour/tour-item';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import { PrintAddressPipe } from 'src/app/pipes/print-address.pipe';
import { ImmoIPTourRouteCheckedChangesService } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-tour-route-checked-changes/immo-ip-tour-route-checked-changes.service';
import { ImmoIPPlacerTourRouteCheckedSubmitService } from 'src/app/services/immo-ip-placer-tour-route-checked-submit/immo-ip-placer-tour-route-checked-submit.service';
import { ImmoIPPlacerTourItemDetailHelper } from '../../tour-item-detail/helper/immo-ip-placer-tour-item-detail-helper';

export class ImmoIPPlacerTourViewHelper {
  private constructor() {}

  static getStoredSelectedView(): SELECTABLE_VIEWS {
    const toReturn: SELECTABLE_VIEWS = <SELECTABLE_VIEWS>(
      localStorage.getItem('immo-ip-placer-selected-view')
    );
    return toReturn == null
      ? SELECTABLE_VIEWS.IMMO_IP_PLACER_TOUR_LIST_VIEW
      : toReturn;
  }

  static setStoredSelectedView(selectedView: SELECTABLE_VIEWS) {
    localStorage.setItem('immo-ip-placer-selected-view', selectedView);
  }

  static processSubProject(subProject: SubProject): void {
    if (subProject.project != null) {
      if (CollectionHelper.isPopulated(subProject.project.projectContacts)) {
        subProject.project.involvedCompanies =
          ProjectHelper.getDtoInvolvedCompanies(subProject.project);
      }
    }
  }

  static processProjectPlacement(
    subProject: SubProject,
    printAddressPipe: PrintAddressPipe,
    calculateTooltip = false,
    formatPhoneNumber: FormatPhoneNumber
  ): void {
    if (subProject.projectPlacement == null) {
      return;
    }
    if (subProject.projectPlacement.appointment) {
      const appointmentTime = <string>(
        (<unknown>subProject.projectPlacement.appointmentTime)
      );
      const appointmentTimeOfDay = StringHelper.isEmpty(appointmentTime)
        ? subProject.projectPlacement.appointmentTimeOfDay
        : appointmentTime.slice(0, 5);

      if (
        subProject.projectPlacement.collectKey &&
        subProject.projectPlacement.collectKey
      ) {
        if (calculateTooltip) {
          const collectKeyAddress =
            ImmoIPPlacerTourViewHelper.getCollectKeyAddressForSubProject(
              subProject,
              printAddressPipe
            );

          subProject.appointmentTooltip = StringHelper.join(
            [appointmentTimeOfDay, collectKeyAddress],
            ' - '
          );
        }
      } else {
        if (calculateTooltip) {
          const appointmentContactDisplay =
            ImmoIPPlacerTourItemDetailHelper.getAppointmentContactDisplay(
              subProject.projectPlacement,
              formatPhoneNumber
            );
          subProject.appointmentTooltip = StringHelper.join(
            [
              appointmentContactDisplay,
              subProject.projectPlacement.needAppointmentInfo,
            ],
            '\n'
          );
          subProject.projectPlacement.needAppointmentInfo;
        }
        subProject.appointmentTime = appointmentTimeOfDay;
      }
    } else if (subProject.projectPlacement.collectKey && calculateTooltip) {
      const collectKeyAddress =
        ImmoIPPlacerTourViewHelper.getCollectKeyAddressForSubProject(
          subProject,
          printAddressPipe
        );
      if (!StringHelper.isEmptyOrWhiteSpace(collectKeyAddress)) {
        subProject.appointmentTooltip = collectKeyAddress;
      }
    }
  }

  private static getCollectKeyAddressForSubProject(
    subProject: SubProject,
    printAddressPipe: PrintAddressPipe
  ) {
    let collectKeyAddress = null;
    if (subProject.project) {
      collectKeyAddress = ProjectHelper.calculateCollectKeyFromAddress(
        subProject,
        printAddressPipe
      );
      if (subProject.projectPlacement.appointmentContact != null) {
        collectKeyAddress = StringHelper.join(
          [
            subProject.projectPlacement.appointmentContact.firstName,
            collectKeyAddress,
          ],
          ', '
        );
      }
    }
    return collectKeyAddress;
  }

  static setTypeDisplays(subProject: SubProject): void {
    switch (subProject.type) {
      case SUB_PROJECT_TYPES.BASE_PROJECT:
        subProject.typeDisplay = 'Plaatsen';
        subProject.typeClass = 'uitgevoerd';
        break;
      case SUB_PROJECT_TYPES.ADJUSTMENT:
        subProject.typeDisplay = 'Aanpassing';
        subProject.typeClass = 'aanpassing';
        break;
      case SUB_PROJECT_TYPES.SOLDRENTED:
        subProject.typeDisplay = 'Verkocht/Verhuurd';
        subProject.typeClass = 'verkocht-verhuurd';
        break;
      case SUB_PROJECT_TYPES.REPO:
        subProject.typeDisplay = 'Weghalen';
        subProject.typeClass = 'weghalen';
        break;
      default:
        throw new Error(`subproject type not recognized: ${subProject.type}`);
    }
  }

  public static setTourItemBackgroundClass(tourItem: TourItem): void {
    if (tourItem.subProject != null) {
      if (this.subProjectExecutedOrDelayed(tourItem.subProject, true)) {
        tourItem.tourItemBackgroundClass = 'executed';
      } else if (this.subProjectNotExecuted(tourItem.subProject)) {
        tourItem.tourItemBackgroundClass = 'not-executed';
      } else {
        tourItem.tourItemBackgroundClass = 'pending';
      }
    } else if (tourItem.immoTask != null) {
      if (tourItem.immoTask.executed) {
        tourItem.tourItemBackgroundClass = 'executed';
      } else if (tourItem.immoTask.executed === false) {
        tourItem.tourItemBackgroundClass = 'not-executed';
      } else {
        tourItem.tourItemBackgroundClass = 'pending';
      }
    }
  }

  public static isExecutedOrDelayed(
    tourItem: TourItem,
    onlyExecuted: boolean = false
  ): boolean {
    if (tourItem.subProject != null) {
      return this.subProjectExecutedOrDelayed(
        tourItem.subProject,
        onlyExecuted
      );
    } else if (tourItem.immoTask != null) {
      return onlyExecuted
        ? !!tourItem.immoTask.executed
        : tourItem.immoTask.executed != null;
    }
  }

  private static subProjectExecutedOrDelayed(
    subProject: SubProject,
    onlyExecuted: boolean
  ): boolean {
    return subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT
      ? this.baseProjectExecutedOrDelayed(subProject, onlyExecuted)
      : this.adjustmentExecutedOrDelayed(subProject, onlyExecuted);
  }

  private static baseProjectExecutedOrDelayed(
    subProject: SubProject,
    onlyExecuted: boolean
  ): boolean {
    return (
      subProject.project != null &&
      CollectionHelper.isPopulated(subProject.project.products) &&
      subProject.project.products.some((p) =>
        p.productPlacement != null && onlyExecuted
          ? !!p.productPlacement.executionComplete
          : p.productPlacement.executionComplete != null
      )
    );
  }

  private static adjustmentExecutedOrDelayed(
    subProject: SubProject,
    onlyExecuted: boolean
  ): boolean {
    return subProject.projectPlacement != null && onlyExecuted
      ? !!subProject.projectPlacement.placementDone
      : subProject.projectPlacement.placementDone != null;
  }

  private static subProjectNotExecuted(subProject: SubProject): boolean {
    return subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT
      ? this.baseProjectNotExecuted(subProject)
      : this.adjustmentNotExecuted(subProject);
  }

  private static baseProjectNotExecuted(subProject: SubProject): boolean {
    return (
      subProject.project != null &&
      CollectionHelper.isPopulated(subProject.project.products) &&
      subProject.project.products.some(
        (p) =>
          p.productPlacement != null &&
          p.productPlacement.executionComplete === false
      )
    );
  }

  private static adjustmentNotExecuted(subProject: SubProject): boolean {
    return (
      subProject.projectPlacement != null &&
      subProject.projectPlacement.placementDone === false
    );
  }

  static async applyLocalTourRouteCheckedChanges(
    tour: Tour,
    immoIPTourRouteCheckedChangesService: ImmoIPTourRouteCheckedChangesService
  ): Promise<void> {
    if (tour == null || tour.id == null) {
      return;
    }
    const tourRouteCheckedChangesObject =
      await immoIPTourRouteCheckedChangesService.getObject(tour.id);
    if (tourRouteCheckedChangesObject != null) {
      tour.routeChecked = tourRouteCheckedChangesObject.routeChecked;
    }
  }

  static async submitTourRouteChecked(
    tour: Tour,
    dateString: string,
    immoIPTourRouteCheckedChangesService: ImmoIPTourRouteCheckedChangesService,
    immoIPPlacerTourRouteCheckedSubmitService: ImmoIPPlacerTourRouteCheckedSubmitService
  ): Promise<void> {
    if (tour == null || tour.id == null) {
      return;
    }
    let tourRouteCheckedChangesObject =
      await immoIPTourRouteCheckedChangesService.getObject(tour.id);
    if (tourRouteCheckedChangesObject == null) {
      tourRouteCheckedChangesObject = {
        id: tour.id,
        dateString: dateString,
        routeChecked: tour.routeChecked,
      };
    } else {
      tourRouteCheckedChangesObject.routeChecked = tour.routeChecked;
    }
    await immoIPPlacerTourRouteCheckedSubmitService.submitTourRouteChecked(
      tourRouteCheckedChangesObject
    );
  }
}

export enum SELECTABLE_VIEWS {
  IMMO_IP_PLACER_TOUR_LIST_VIEW = 'immo-ip-placer-tour-list-view',
  IMMO_IP_PLACER_TOUR_ROUTE_VIEW = 'immo-ip-placer-tour-route-view',
}
